import React, { useMemo, useRef, useState } from "react";
import { Form, Row, Col, DatePicker, Typography, Table, Select, Checkbox, message } from 'antd';
import moment from 'moment';
import { useDispatch } from "react-redux";
import { getRangeOptions, getPaginationDetails, getDefaultPageSize } from './reports.module';
import { getBusServiceCollectionReportAction } from './../../actions/reports.action';
import { getAPIDateFormat, getLocalDateAndTime } from './../../utils/date.utils';
import { isValid, isValidArray } from "../../utils/utilities";
import { roundNumber } from "../../utils/math.utils";
import { Printer } from './../../components/custom-components/custom-component';
import { loadingAction } from './../../actions/loading-actions';
import './bus-service-collection.report.scss';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const allowedMaxDaysRange = 31;
const xs = 24, sm = 24, md = 12, lg = 12, xl = 12, xxl = 12;

const STATUS_CANCELLED = 'Cancelled';
export const TICKET_STATUS = { ALL: 'ALL', CONFIRMED: 'CONFIRMED', CANCELLED: 'CANCELLED' };
const TICKET_STATUS_LABELS = { ALL: 'All', CONFIRMED: 'Confirmed', CANCELLED: 'Cancelled' };
const OTA_PRIVILEGES = ['OTA', 'Prepaid-OTA'];
const WEBSITE_PRIVILEGES = ['Website'];
const AGENT_PRIVILEGES = ['Booking Agent', 'Agent BT', 'Sub Agent', 'offline-agent'];

// ============================================================
// Formatters / helpers
// ============================================================
const toNum = (n) => {
    const v = Number(n);
    return isNaN(v) ? 0 : v;
};
const fmtAmount = (n) => toNum(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtINR = (n) => '₹' + fmtAmount(n);
const fmtCount = (n) => toNum(n).toLocaleString('en-IN');
const fmtDate = (d) => isValid(d) ? moment(d).format('DD/MM/YYYY') : '';
const hasText = (v) => isValid(v) && String(v).trim() !== '';
const getServiceLabel = (trip) => (hasText(trip.serviceName) ? trip.serviceName : (hasText(trip.description) ? trip.description : `Schedule ${trip.scheduleId}`));
const getRouteLabel = (trip) => `${trip.origin || ''} to ${trip.destination || ''}`;
const getCoachLabel = (trip) => trip.vehicleNumber || '';
const getAgentTypeLabel = (ticket) => ticket.agentPrivilege || 'Unknown';

const isCancelledTicket = (ticket) => ticket.status === STATUS_CANCELLED;

// Direct discount of the seat (includes OTA operator offers) plus any miles discount.
export const getDirectDiscount = (ticket) => toNum(ticket.directDiscount) + toNum(ticket.milesDiscount);

// Online / PoS / net banking payments (e.g. agent UPI bookings) show the payment, else the booking channel.
const PAYMENT_TYPE_PAY_MODES = { 'Online': 'by-online (UPI)', 'PoS Machine': 'by-pos', 'Internet banking': 'by-internet-banking' };

export const getPayMode = (ticket) => {
    if (OTA_PRIVILEGES.includes(ticket.agentPrivilege)) return 'on-api';
    if (WEBSITE_PRIVILEGES.includes(ticket.agentPrivilege)) return 'e-ticket';
    const paymentType = hasText(ticket.paymentType) ? String(ticket.paymentType).trim() : '';
    if (PAYMENT_TYPE_PAY_MODES[paymentType]) return PAYMENT_TYPE_PAY_MODES[paymentType];
    if (AGENT_PRIVILEGES.includes(ticket.agentPrivilege)) return 'on-agent';
    return 'by-cash';
};

// ============================================================
// Aggregation
// ============================================================
const EMPTY_TOTALS = {
    totalSeats: 0, seatCount: 0, cancelledSeats: 0,
    ticketFare: 0, gst: 0, totalAmount: 0, directDiscount: 0, agentComm: 0,
    refundAmount: 0, cancellationPenalty: 0,
};
const AMOUNT_KEYS = ['ticketFare', 'gst', 'totalAmount', 'directDiscount', 'agentComm',
    'refundAmount', 'cancellationPenalty'];

const roundAmounts = (totals) => {
    AMOUNT_KEYS.forEach((key) => { totals[key] = roundNumber(totals[key]); });
    return totals;
};

/*
  Collection of one service from its seats:
  - Ticket Fare and GST are of every seat sold (confirmed + cancelled).
  - Total Amount = Ticket Fare + GST.
  - Agent Comm is the commission on confirmed seats plus the agents' share of the
    cancellation penalty; Refund Amount is what was returned to the customer and
    Cancellation Penalty the charges retained.
*/
export const aggregateTickets = (tickets) => {
    const totals = { ...EMPTY_TOTALS };
    (tickets || []).forEach((ticket) => {
        const cancelled = isCancelledTicket(ticket);
        totals.seatCount += 1;
        totals.ticketFare += toNum(ticket.fare);
        totals.gst += toNum(ticket.gst);
        totals.directDiscount += getDirectDiscount(ticket);
        if (cancelled) {
            totals.cancelledSeats += 1;
            totals.agentComm += toNum(ticket.penaltyCommission);
            totals.refundAmount += toNum(ticket.amountPaid) - toNum(ticket.cancellationPenalty);
            totals.cancellationPenalty += toNum(ticket.cancellationPenalty);
        } else {
            totals.agentComm += toNum(ticket.commission);
        }
    });
    totals.totalAmount = totals.ticketFare + totals.gst;
    return roundAmounts(totals);
};

// Net collection = Total Amount − Direct Discount − Agent Comm − Refund Amount
export const getNetCollection = (row) => roundNumber(
    toNum(row.totalAmount) - toNum(row.directDiscount) - toNum(row.agentComm) - toNum(row.refundAmount)
);

export const sumRows = (rows) => {
    // A row is one trip (service on a travel date); distinct schedules are the services.
    const totals = { ...EMPTY_TOTALS, trips: 0, services: 0 };
    const scheduleIds = new Set();
    (rows || []).forEach((row) => {
        totals.trips += 1;
        scheduleIds.add(row.scheduleId);
        Object.keys(EMPTY_TOTALS).forEach((key) => { totals[key] += toNum(row[key]); });
    });
    totals.services = scheduleIds.size;
    roundAmounts(totals);
    totals.netCollection = getNetCollection(totals);
    return totals;
};

export const filterTickets = (tickets, { agentTypes, excludeNonReported, ticketStatus }) => {
    if (!isValidArray(tickets)) return [];
    const filterAgentTypes = isValidArray(agentTypes) && agentTypes.length > 0;
    return tickets.filter((ticket) => {
        if (filterAgentTypes && !agentTypes.includes(getAgentTypeLabel(ticket))) return false;
        if (ticketStatus === TICKET_STATUS.CONFIRMED && isCancelledTicket(ticket)) return false;
        if (ticketStatus === TICKET_STATUS.CANCELLED && !isCancelledTicket(ticket)) return false;
        if (excludeNonReported && !isCancelledTicket(ticket) && !ticket.boarded) return false;
        return true;
    });
};

export const buildServiceRows = (trips, tickets, { scheduleIds, vehicleNumbers }) => {
    if (!isValidArray(trips)) return [];
    const ticketsByTrip = {};
    (tickets || []).forEach((ticket) => {
        if (!ticketsByTrip[ticket.tripId]) ticketsByTrip[ticket.tripId] = [];
        ticketsByTrip[ticket.tripId].push(ticket);
    });
    const filterSchedules = isValidArray(scheduleIds) && scheduleIds.length > 0;
    const filterVehicles = isValidArray(vehicleNumbers) && vehicleNumbers.length > 0;
    return trips
        .filter((trip) => !filterSchedules || scheduleIds.includes(trip.scheduleId))
        .filter((trip) => !filterVehicles || vehicleNumbers.includes(getCoachLabel(trip)))
        .map((trip) => ({
            ...trip,
            ...aggregateTickets(ticketsByTrip[trip.tripId]),
            totalSeats: toNum(trip.seatsCapacity),
            tickets: ticketsByTrip[trip.tripId] || [],
        }));
};

// ============================================================
// Detailed view (CSV): bookings and cancellations of every service, PNR wise
// ============================================================
const csvCell = (value) => {
    if (value == null) return '""';
    const text = String(value);
    return /[",\n]/.test(text) || text === '' ? `"${text.replace(/"/g, '""')}"` : text;
};
const csvLine = (cells) => cells.map(csvCell).join(',');

const getBranchName = (ticket) => {
    if (WEBSITE_PRIVILEGES.includes(ticket.agentPrivilege)) return '';
    return hasText(ticket.agentBranch) ? ticket.agentBranch : (global.operatorName || '');
};

const groupByPnr = (tickets) => {
    const groups = [];
    const byPnr = {};
    tickets.forEach((ticket) => {
        if (!byPnr[ticket.pnr]) {
            byPnr[ticket.pnr] = { ...ticket, seats: [], list: [] };
            groups.push(byPnr[ticket.pnr]);
        }
        byPnr[ticket.pnr].seats.push(ticket.seatNumber);
        byPnr[ticket.pnr].list.push(ticket);
    });
    return groups;
};
const sumOf = (list, fn) => roundNumber(list.reduce((acc, item) => acc + toNum(fn(item)), 0));
const seatLabel = (seats) => `${seats.join(',')} (${seats.length})`;

// A seat cancelled after being marked not boarded is listed with the bookings.
export const isExportedBooking = (ticket) => !isCancelledTicket(ticket) || ticket.notBoarded === true;

export const BOOKINGS_CSV_HEADER = ['PNR No', 'Branch Name', 'Issued By', 'Onbehalf Of', 'Origin', 'Destination',
    'Seat Number', 'Fare', 'Discount', 'Offer Discount', 'GST', 'Net Amount', 'Pay Mode'];

export const hasExportedTickets = (rows) => (rows || []).some((row) => isValidArray(row.tickets) && row.tickets.length > 0);

export const buildDetailedCsv = (rows) => {
    const lines = ['Bus service collection detailed data', '""'];
    rows.filter((row) => isValidArray(row.tickets) && row.tickets.length > 0).forEach((row) => {
        const bookings = groupByPnr(row.tickets.filter(isExportedBooking));
        const cancellations = groupByPnr(row.tickets.filter((t) => !isExportedBooking(t)));
        let netCollection = 0;

        lines.push(csvLine([`Service: ${getServiceLabel(row)}`, `Vehicle Number: ${getCoachLabel(row)}`, `Travel Date: ${fmtDate(row.travelDate)}`]));
        if (bookings.length > 0) {
            lines.push('Bookings');
            lines.push(csvLine(BOOKINGS_CSV_HEADER));
            const total = { seats: 0, fare: 0, discount: 0, offerDiscount: 0, gst: 0, net: 0 };
            bookings.forEach((pnr) => {
                // OTA operator offers (stored as direct discount) are the Offer Discount.
                const isOta = OTA_PRIVILEGES.includes(pnr.agentPrivilege);
                const fare = sumOf(pnr.list, (t) => t.fare);
                const offerDiscount = isOta ? sumOf(pnr.list, (t) => t.directDiscount) : 0;
                const discount = isOta ? sumOf(pnr.list, (t) => t.milesDiscount) : sumOf(pnr.list, getDirectDiscount);
                const gst = sumOf(pnr.list, (t) => t.gst);
                const agentComm = sumOf(pnr.list, (t) => t.commission);
                const net = roundNumber(fare - discount - offerDiscount + gst - agentComm);
                total.seats += pnr.seats.length; total.fare += fare; total.discount += discount;
                total.offerDiscount += offerDiscount; total.gst += gst; total.net += net;
                lines.push(csvLine([pnr.pnr, getBranchName(pnr), pnr.agentName || '', pnr.agentName || '', pnr.origin || '',
                    pnr.destination || '', seatLabel(pnr.seats), fare, discount, offerDiscount, gst, net, getPayMode(pnr)]));
            });
            lines.push(csvLine(['Total', '', '', '', '', '', `${total.seats} seat(s)`, roundNumber(total.fare), roundNumber(total.discount),
                roundNumber(total.offerDiscount), roundNumber(total.gst), roundNumber(total.net), '']));
            lines.push('""');
            netCollection += total.net;
        }
        if (cancellations.length > 0) {
            lines.push('Cancellations');
            lines.push(csvLine(['PNR', 'Branch', 'Agent', 'Origin', 'Destination', 'Cancelled Date', 'Cancelled By',
                'Seat Numbers', 'Ticket Fare', 'GST', 'Cancellation Penalty', 'Refund Amount', 'Mode']));
            const total = { seats: 0, fare: 0, penalty: 0, gst: 0, refunds: 0, agentShare: 0 };
            cancellations.forEach((pnr) => {
                const fare = sumOf(pnr.list, (t) => t.fare);
                const penalty = sumOf(pnr.list, (t) => t.cancellationPenalty);
                const gst = sumOf(pnr.list, (t) => t.gst);
                const refunds = sumOf(pnr.list, (t) => toNum(t.amountPaid) - toNum(t.cancellationPenalty));
                total.seats += pnr.seats.length; total.fare += fare; total.penalty += penalty;
                total.gst += gst; total.refunds += refunds; total.agentShare += sumOf(pnr.list, (t) => t.penaltyCommission);
                lines.push(csvLine([pnr.pnr, getBranchName(pnr), pnr.agentName || '', pnr.origin || '', pnr.destination || '',
                    isValid(pnr.cancellationTime) ? moment(pnr.cancellationTime).format('YYYY-MM-DD') : '', pnr.cancelledBy || '',
                    seatLabel(pnr.seats), fare, gst, penalty, refunds, getPayMode(pnr)]));
            });
            lines.push(csvLine(['Total', '', '', '', '', '', '', `${total.seats} seat(s)`, roundNumber(total.fare),
                roundNumber(total.gst), roundNumber(total.penalty), roundNumber(total.refunds), '']));
            lines.push('""');
            netCollection += total.penalty - total.agentShare;
        }
        lines.push(csvLine(['', '', '', '', '', '', '', '', '', '', '', 'Net Collection', roundNumber(netCollection)]));
        lines.push('""');
        lines.push('""');
    });
    return lines.join('\n');
};

const downloadDetailedCsv = (filters, rows) => {
    const blob = new Blob([buildDetailedCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bus-service-collection-detailed-${filters.fromDate}-to-${filters.toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// ============================================================
// Inline icons (same set as the sales / occupancy reports)
// ============================================================
const Icon = {
    search: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/></svg>,
    download: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 2v9m-3-3 3 3 3-3M2 13h12"/></svg>,
    caretRight: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 4 4 4-4 4"/></svg>,
    run: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 3l8 5-8 5z"/></svg>,
    print: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 6V2h8v4M4 12H2.5A.5.5 0 0 1 2 11.5v-5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 .5.5v5a.5.5 0 0 1-.5.5H12"/><path d="M4 9.5h8V14H4z"/></svg>,
};

// ============================================================
// Columns: labels follow the other agent portal reports
// ============================================================
const amountColumn = (title, key, isDeduction = false) => ({
    title, key, dataIndex: key, align: 'center', className: 'col-center',
    sorter: (a, b) => toNum(a[key]) - toNum(b[key]),
    render: (text) => <span className={`tx-amount ${isDeduction ? 'deduction' : ''}`}>{fmtAmount(text)}</span>,
});
const countColumn = (title, key) => ({
    title, key, dataIndex: key, align: 'center', className: 'col-center',
    sorter: (a, b) => toNum(a[key]) - toNum(b[key]),
    render: (text) => <span className="tx-count">{fmtCount(text)}</span>,
});

export const REPORT_FIELDS = [
    { title: 'Service', key: 'service', value: getServiceLabel },
    { title: 'Route', key: 'route', value: getRouteLabel },
    { title: 'Vehicle Number', key: 'vehicleNumber', value: getCoachLabel },
    { title: 'Travel Date', key: 'travelDate', value: (row) => fmtDate(row.travelDate) },
    { title: 'Total Seats', key: 'totalSeats' },
    { title: 'Seat Count', key: 'seatCount' },
    { title: 'Cancelled Seats', key: 'cancelledSeats' },
    { title: 'Ticket Fare', key: 'ticketFare' },
    { title: 'GST', key: 'gst' },
    { title: 'Total Amount', key: 'totalAmount' },
    { title: 'Direct Discount', key: 'directDiscount' },
    { title: 'Agent Comm', key: 'agentComm' },
    { title: 'Refund Amount', key: 'refundAmount' },
    { title: 'Cancellation Penalty', key: 'cancellationPenalty' },
];
const getFieldValue = (field, row) => (field.value ? field.value(row) : row[field.key]);

const buildDisplayColumns = () => [
    {
        title: 'Service', key: 'service', className: 'col-service',
        sorter: (a, b) => getServiceLabel(a).localeCompare(getServiceLabel(b)),
        render: (_t, record) => <div className="tx-route"><div className="ot">{getServiceLabel(record)}</div></div>,
    },
    {
        title: 'Route', key: 'route', className: 'col-service',
        render: (_t, record) => (
            <div className="bsc-route"><strong>{record.origin}</strong> to <strong>{record.destination}</strong></div>
        ),
    },
    { title: 'Vehicle Number', key: 'vehicleNumber', align: 'center', className: 'col-center', render: (_t, record) => <span className="tx-date">{getCoachLabel(record)}</span> },
    {
        title: 'Travel Date', key: 'travelDate', align: 'center', className: 'col-center',
        sorter: (a, b) => String(a.travelDate || '').localeCompare(String(b.travelDate || '')),
        render: (_t, record) => <span className="tx-date">{fmtDate(record.travelDate)}</span>,
    },
    countColumn('Total Seats', 'totalSeats'),
    countColumn('Seat Count', 'seatCount'),
    countColumn('Cancelled Seats', 'cancelledSeats'),
    amountColumn('Ticket Fare', 'ticketFare'),
    amountColumn('GST', 'gst'),
    amountColumn('Total Amount', 'totalAmount'),
    amountColumn('Direct Discount', 'directDiscount', true),
    amountColumn('Agent Comm', 'agentComm', true),
    amountColumn('Refund Amount', 'refundAmount', true),
    amountColumn('Cancellation Penalty', 'cancellationPenalty'),
];

const getPeriodLabel = (filters) => {
    if (!filters.fromDate || !filters.toDate) return '';
    return filters.fromDate === filters.toDate
        ? fmtDate(filters.fromDate)
        : `${fmtDate(filters.fromDate)} to ${fmtDate(filters.toDate)}`;
};

// ============================================================
// Sub-components
// ============================================================
function PageHeader({ onPrint, onExport, canExport, printerNode }) {
    return (
        <div className="pageheader">
            <div>
                <div className="breadcrumb">
                    <span>Reports</span>{Icon.caretRight}<span>Revenue</span>{Icon.caretRight}
                    <span style={{ color: 'var(--ink-2)' }}>Bus Service Collection</span>
                </div>
                <h1>Bus Service Collection</h1>
            </div>
            <div className="pageheader-actions">
                {printerNode}
                <button type="button" className="btn print" disabled={!canExport} onClick={onPrint}>
                    {Icon.print}Print
                </button>
                <button type="button" className="btn primary" disabled={!canExport} onClick={onExport}>
                    {Icon.download}Export
                </button>
            </div>
        </div>
    );
}

function FilterCard({ form, onSubmit, onReset, running }) {
    return (
        <div className="card">
            <Form form={form} onFinish={onSubmit}>
                <div className="filter-grid">
                    <div className="field field-rangepicker-wrap" style={{ gridColumn: '1 / span 5', gridRow: 1 }}>
                        <div className="field-label">Travel Date Range</div>
                        <Form.Item name="dateRange" rules={[
                            { required: true, message: 'Please select a travel date range' },
                            () => ({
                                validator(_, value) {
                                    if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) return Promise.resolve();
                                    return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                },
                            }),
                        ]}>
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} format="DD MMM YYYY" />
                        </Form.Item>
                    </div>

                    <div className="filter-actions" style={{ gridColumn: '1 / -1', gridRow: 2, display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                        <button type="button" className="btn" onClick={onReset} disabled={running}>Reset</button>
                        <button type="submit" className="btn primary" disabled={running}>
                            {Icon.run}{running ? 'Running…' : 'Run Report'}
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}

const MultiSelect = ({ label, placeholder, emptyPlaceholder, options, value, onChange }) => (
    <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
        <div className="field-label">{label}</div>
        <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={options.length === 0 ? emptyPlaceholder : placeholder}
            value={value}
            onChange={(vals) => onChange(vals || [])}
            maxTagCount={2}
            maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            optionFilterProp="children"
            filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
            disabled={options.length === 0}
            style={{ width: '100%' }}
        >
            {options.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
        </Select>
    </div>
);

function RefineBar({ agentTypeOptions, scheduleOptions, vehicleOptions, filters, onChange }) {
    const hasActiveFilter = filters.agentTypes.length > 0 || filters.scheduleIds.length > 0
        || filters.vehicleNumbers.length > 0 || filters.excludeNonReported || filters.ticketStatus !== TICKET_STATUS.ALL;
    return (
        <div className="refine-bar">
            <MultiSelect label="User/Agent Type" placeholder="All" emptyPlaceholder="No user types in result"
                options={agentTypeOptions} value={filters.agentTypes} onChange={(agentTypes) => onChange({ agentTypes })} />
            <MultiSelect label="Services" placeholder="- All -" emptyPlaceholder="No services in result"
                options={scheduleOptions} value={filters.scheduleIds} onChange={(scheduleIds) => onChange({ scheduleIds })} />
            <MultiSelect label="Coach Number" placeholder="All" emptyPlaceholder="No coaches in result"
                options={vehicleOptions} value={filters.vehicleNumbers} onChange={(vehicleNumbers) => onChange({ vehicleNumbers })} />
            <div className="field">
                <div className="field-label">Ticket Status</div>
                <div className="segmented">
                    {Object.values(TICKET_STATUS).map((status) => (
                        <button key={status} type="button"
                            className={filters.ticketStatus === status ? 'active' : ''}
                            onClick={() => onChange({ ticketStatus: status })}>{TICKET_STATUS_LABELS[status]}</button>
                    ))}
                </div>
            </div>
            <div className="field bsc-checkbox-field">
                <Checkbox checked={filters.excludeNonReported} onChange={(e) => onChange({ excludeNonReported: e.target.checked })}>
                    Exclude Non Reported Seat
                </Checkbox>
            </div>
            {hasActiveFilter && (
                <button type="button" className="btn" onClick={() => onChange({
                    agentTypes: [], scheduleIds: [], vehicleNumbers: [], excludeNonReported: false, ticketStatus: TICKET_STATUS.ALL,
                })}>Clear</button>
            )}
        </div>
    );
}

function FilterSummaryChips({ filters, generatedAt, labels }) {
    return (
        <div className="filter-summary">
            <span className="chip"><span className="label">Travel Date</span><strong>{getPeriodLabel(filters)}</strong></span>
            <span className="chip"><span className="label">User/Agent Type</span><strong>{labels.agentTypeLabel}</strong></span>
            <span className="chip"><span className="label">Services</span><strong>{labels.scheduleLabel}</strong></span>
            <span className="chip"><span className="label">Coach</span><strong>{labels.vehicleLabel}</strong></span>
            <span className="chip"><span className="label">Ticket Status</span><strong>{TICKET_STATUS_LABELS[filters.ticketStatus]}</strong></span>
            {filters.excludeNonReported && <span className="chip"><strong>Non reported seats excluded</strong></span>}
            {generatedAt && <span className="gen">Generated {generatedAt}</span>}
        </div>
    );
}

export function SummaryCard({ totals }) {
    const rows = [
        { code: 'A', metric: 'Total Amount (Ticket Fare + GST)', value: fmtINR(totals.totalAmount) },
        { code: 'B', metric: 'Direct Discount', value: fmtINR(totals.directDiscount) },
        { code: 'C', metric: 'Agent Comm', value: fmtINR(totals.agentComm) },
        { code: 'D', metric: 'Refund Amount', value: fmtINR(totals.refundAmount) },
        { code: 'E', metric: 'Net Collection (A − B − C − D)', value: fmtINR(totals.netCollection), subtotal: true },
    ];
    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Collection Summary</div>
            </div>
            <div className="hero-metric">
                <div>
                    <div className="l">Net collection</div>
                    <div className="v">{fmtINR(totals.netCollection)}</div>
                    <div className="hero-counts">
                        <span className="hero-count confirmed"><strong>{fmtCount(totals.seatCount)}</strong> seats</span>
                        <span className="hero-count cancelled"><strong>{fmtCount(totals.cancelledSeats)}</strong> cancelled</span>
                    </div>
                    <div className="formula">
                        Total Amount − Direct Discount − Agent Comm − Refund Amount across {fmtCount(totals.trips)} {totals.trips === 1 ? 'trip' : 'trips'} of {fmtCount(totals.services)} {totals.services === 1 ? 'service' : 'services'}
                    </div>
                </div>
                <div className="right">
                    <div className="pair">
                        <div className="stat">
                            <div className="l">Cancellation Penalty</div>
                            <div className="v">{fmtINR(totals.cancellationPenalty)}</div>
                            <div className="sub">Retained on cancelled seats</div>
                        </div>
                        <div className="stat">
                            <div className="l">GST</div>
                            <div className="v">{fmtINR(totals.gst)}</div>
                            <div className="sub">On all seats sold</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="summary-section">
                <div className="summary-section-title">Collection</div>
                <div className="summary-table kv">
                    <div className="h">Metric</div>
                    <div className="h right">Value</div>
                    {rows.map((r) => (
                        <div key={r.code} className={`row ${r.subtotal ? 'subtotal' : ''}`}>
                            <div className="cell metric"><span className="code">{r.code}.</span>{r.metric}</div>
                            <div className="cell value">{r.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const renderTotalCells = (label, totals) => {
    const cells = [
        <Table.Summary.Cell key="label" index={0}>{label}</Table.Summary.Cell>,
        <Table.Summary.Cell key="route" index={1}></Table.Summary.Cell>,
        <Table.Summary.Cell key="vehicle" index={2}></Table.Summary.Cell>,
        <Table.Summary.Cell key="date" index={3}></Table.Summary.Cell>,
        <Table.Summary.Cell key="totalSeats" index={4}></Table.Summary.Cell>,
        <Table.Summary.Cell key="seats" index={5} align="center" className="seat-total">{fmtCount(totals.seatCount)} Seat(s)</Table.Summary.Cell>,
        <Table.Summary.Cell key="cancelled" index={6} align="center" className="seat-total">{fmtCount(totals.cancelledSeats)} Seat(s)</Table.Summary.Cell>,
    ];
    const deductions = ['directDiscount', 'agentComm', 'refundAmount'];
    AMOUNT_KEYS.forEach((key, i) => {
        cells.push(
            <Table.Summary.Cell key={key} index={7 + i} align="center">
                <span className={deductions.includes(key) ? 'deduction' : ''}>{fmtAmount(totals[key])}</span>
            </Table.Summary.Cell>
        );
    });
    return cells;
};

export function ServicesCard({ rows, totals, paginationSize, setPaginationSize }) {
    const [search, setSearch] = useState('');

    const data = useMemo(() => {
        if (!isValidArray(rows)) return [];
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((x) => [getServiceLabel(x), getRouteLabel(x), getCoachLabel(x), fmtDate(x.travelDate)]
            .some((f) => f && String(f).toLowerCase().includes(q)));
    }, [rows, search]);

    const columns = useMemo(() => buildDisplayColumns(), []);
    const isFiltered = data.length !== (rows || []).length;
    const visibleTotals = useMemo(() => (isFiltered ? sumRows(data) : totals), [data, totals, isFiltered]);

    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Services</div>
                <div className="tabs">
                    <button type="button" className="active">
                        Trips <span className="count">{fmtCount(data.length)}</span>
                    </button>
                </div>
            </div>
            <div className="card-sub">
                One row per trip in the selected travel dates with the ticket amount, agent commission, cancellation and net collection (incl. of all taxes).
                Direct Discount, Agent Comm and Refund Amount are deductions and shown in red.
            </div>

            <div className="table-toolbar">
                <div className="search">
                    {Icon.search}
                    <input
                        placeholder="Search by service, route, coach, travel date…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <Table
                className="tx-table"
                rowKey={(r) => r.tripId}
                columns={columns}
                dataSource={data}
                pagination={getPaginationDetails(true, () => {}, paginationSize, setPaginationSize)}
                locale={{ emptyText: <div className="empty-state">No services match the current filters.</div> }}
                size="middle"
                summary={(pageData) => (
                    <Table.Summary>
                        <Table.Summary.Row className="page-total">{renderTotalCells('Page Total', sumRows(pageData))}</Table.Summary.Row>
                        {data.length > pageData.length && (
                            <Table.Summary.Row className="grand-total">{renderTotalCells('Grand Total', visibleTotals)}</Table.Summary.Row>
                        )}
                    </Table.Summary>
                )}
            />
        </div>
    );
}

// ============================================================
// Main component
// ============================================================
const getInitialFilters = () => ({
    fromDate: undefined, toDate: undefined,
    agentTypes: [], scheduleIds: [], vehicleNumbers: [], excludeNonReported: false, ticketStatus: TICKET_STATUS.ALL,
});

const getSelectionLabel = (selected, options) => {
    if (!isValidArray(selected) || selected.length === 0) return 'All';
    if (selected.length > 2) return `${selected.length} selected`;
    return selected.map((v) => (options.find((o) => o.value === v) || {}).label || v).join(', ');
};

export const BusServiceCollectionReport = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [showReport, setShowReport] = useState(false);
    const [running, setRunning] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [trips, setTrips] = useState([]);
    const [tickets, setTickets] = useState([]);

    const [paginationSize, setPaginationSize] = useState(getDefaultPageSize);
    const [filters, setFilters] = useState(getInitialFilters());

    const setLoading = (bool) => dispatch(loadingAction(bool));

    const onSuccess = (data) => {
        const reportTrips = isValidArray(data.trips) ? data.trips : [];
        setTrips(reportTrips);
        setTickets(isValidArray(data.tickets) ? data.tickets : []);
        setFilters((f) => ({ ...f, agentTypes: [], scheduleIds: [], vehicleNumbers: [], excludeNonReported: false, ticketStatus: TICKET_STATUS.ALL }));
        setGeneratedAt(getLocalDateAndTime(new Date()));
        setShowReport(true);
        setRunning(false);
        setLoading(false);
        if (reportTrips.length === 0) {
            message.info('No services found for the selected period');
        }
    };

    const onFailure = (errorMessage) => {
        setRunning(false);
        setLoading(false);
        message.error(errorMessage || 'Failed to load bus service collection report');
    };

    const onRunReport = (values) => {
        const { dateRange } = values;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setShowReport(false);
        setRunning(true);
        setLoading(true);
        setFilters((f) => ({ ...f, fromDate, toDate }));
        getBusServiceCollectionReportAction(fromDate, toDate, onSuccess, onFailure);
    };

    const onReset = () => {
        form.resetFields();
        setTrips([]);
        setTickets([]);
        setShowReport(false);
        setGeneratedAt(null);
        setFilters(getInitialFilters());
    };

    // Refine options are derived from the loaded result.
    const agentTypeOptions = useMemo(() => [...new Set(tickets.map(getAgentTypeLabel))].sort()
        .map((v) => ({ value: v, label: v })), [tickets]);
    const scheduleOptions = useMemo(() => {
        const byId = {};
        trips.forEach((trip) => {
            if (isValid(trip.scheduleId) && !byId[trip.scheduleId]) {
                byId[trip.scheduleId] = { value: trip.scheduleId, label: getServiceLabel(trip) };
            }
        });
        return Object.values(byId).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [trips]);
    const vehicleOptions = useMemo(() => [...new Set(trips.map(getCoachLabel).filter(hasText))].sort()
        .map((v) => ({ value: v, label: v })), [trips]);

    const filteredTickets = useMemo(() => filterTickets(tickets, filters), [tickets, filters]);
    const rows = useMemo(() => buildServiceRows(trips, filteredTickets, filters), [trips, filteredTickets, filters]);
    const totals = useMemo(() => sumRows(rows), [rows]);

    const labels = {
        agentTypeLabel: getSelectionLabel(filters.agentTypes, agentTypeOptions),
        scheduleLabel: getSelectionLabel(filters.scheduleIds, scheduleOptions),
        vehicleLabel: getSelectionLabel(filters.vehicleNumbers, vehicleOptions),
    };

    // Export downloads the detailed view: bookings and cancellations of every trip, PNR wise.
    const onExport = () => {
        if (!hasExportedTickets(rows)) {
            message.warning('No bookings or cancellations to export');
            return;
        }
        downloadDetailedCsv(filters, rows);
    };

    // Printer only renders the print layout; its own button is replaced by the page header's Print button.
    const printHandlerRef = useRef(null);
    const onPrint = () => printHandlerRef.current && printHandlerRef.current();

    const printerNode = (
        <Printer
            hideInternalButton={true}
            printHandlerRef={printHandlerRef}
            showComponent={false}
            zoomValue={0.6}
            renderComponent={() => <RenderReport filters={filters} rows={rows} totals={totals} labels={labels} />}
        />
    );

    return (
        <div className="report-inner-div sales-report-v2 bus-service-collection-report">
            <PageHeader onPrint={onPrint} onExport={onExport} canExport={showReport} printerNode={printerNode} />
            <FilterCard
                form={form}
                onSubmit={onRunReport}
                onReset={onReset}
                running={running}
            />
            {showReport && (
                <RefineBar
                    agentTypeOptions={agentTypeOptions}
                    scheduleOptions={scheduleOptions}
                    vehicleOptions={vehicleOptions}
                    filters={filters}
                    onChange={(change) => setFilters((f) => ({ ...f, ...change }))}
                />
            )}
            {showReport && <FilterSummaryChips filters={filters} generatedAt={generatedAt} labels={labels} />}
            {showReport && <SummaryCard totals={totals} />}
            {showReport && (
                <ServicesCard
                    rows={rows}
                    totals={totals}
                    paginationSize={paginationSize}
                    setPaginationSize={setPaginationSize}
                />
            )}
        </div>
    );
};

// ============================================================
// Print layout — classic bordered table rendered by the Printer.
// ============================================================
const printColumns = REPORT_FIELDS.map((field) => ({
    title: field.title, key: field.key,
    render: (_t, row) => {
        const value = getFieldValue(field, row);
        return AMOUNT_KEYS.includes(field.key) ? fmtAmount(value) : value;
    },
}));

export const RenderReport = ({ filters, rows, totals, labels }) => {
    return (
        <div className="bus-service-collection-print">
            <Row justify="center" className="bsc-print-title"><Col><Text strong>{global.operatorName}</Text></Col></Row>
            <Row justify="center">
                <Col><Text italic>Printed By <Text strong>{global.name || global.userName}</Text> On: <Text strong>{getLocalDateAndTime(new Date())}</Text></Text></Col>
            </Row>
            <Row justify="center" className="bsc-print-title">
                <Col><Text><Text strong>BUS Service Collection</Text> For {getPeriodLabel(filters)} for {labels.scheduleLabel === 'All' ? 'all Services' : labels.scheduleLabel}</Text></Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Travel Date : </Text>{getPeriodLabel(filters)}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>User/Agent Type : </Text>{labels.agentTypeLabel}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Coach Number : </Text>{labels.vehicleLabel}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Exclude Non Reported Seat : </Text>{filters.excludeNonReported ? 'Yes' : 'No'}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Ticket Status : </Text>{TICKET_STATUS_LABELS[filters.ticketStatus]}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className="marginTop">
                <Col span={24}>
                    <Table
                        className="bordered-table"
                        rowKey={(r) => r.tripId}
                        columns={printColumns}
                        dataSource={rows}
                        pagination={false}
                        summary={() => (
                            <Table.Summary.Row>
                                {REPORT_FIELDS.map((field, index) => (
                                    <Table.Summary.Cell key={field.key} index={index}>
                                        {index === 0 ? <Text strong>Total</Text>
                                            : AMOUNT_KEYS.includes(field.key) ? <Text strong>{fmtAmount(totals[field.key])}</Text>
                                            : ['seatCount', 'cancelledSeats'].includes(field.key) ? <Text strong>{fmtCount(totals[field.key])} Seat(s)</Text>
                                            : null}
                                    </Table.Summary.Cell>
                                ))}
                            </Table.Summary.Row>
                        )}
                    />
                </Col>
            </Row>
        </div>
    );
};
